import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  LANGUAGE_LABELS,
  LANGUAGE_OPTIONS,
  type Language,
} from '@/admin/languages';
import { PROFILE_TYPE_LABELS } from '@/admin/sitecontent/conditions';
import { parseLanguage, tableSearchKeys } from '@/admin/sitecontent/listSearch';
import { NoticeStateBadge } from '@/admin/sitecontent/NoticeStateBadge';
import buttonStyles from '@/admin/styles/button.module.css';
import { createTablePage } from '@/admin/ui/createTablePage';
import { Select } from '@/admin/ui/Form';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Item = components['schemas']['AdminHeaderNoticeItem'];
type State = components['schemas']['AdminHeaderNoticeState'];

const STATES: State[] = ['DRAFT', 'SCHEDULED', 'ACTIVE', 'EXPIRED'];

const columnHelper = createColumnHelper<Item>();

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
    cell: (c) => c.getValue() || '—',
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor('state', {
    header: () => i18n.t('admin.notices.col.state'),
    cell: (c) => <NoticeStateBadge state={c.getValue()} />,
  }),
  columnHelper.accessor('starts_at', {
    header: () => i18n.t('admin.notices.starts'),
    cell: (c) => formatDate(c.getValue()),
  }),
  columnHelper.accessor('expires_at', {
    header: () => i18n.t('admin.notices.expires'),
    cell: (c) => formatDate(c.getValue()),
  }),
  columnHelper.accessor('audience_profile_types', {
    header: () => i18n.t('admin.notices.audience'),
    cell: (c) =>
      c.getValue().length === 0
        ? i18n.t('admin.notices.everyone')
        : c
            .getValue()
            .map((type) => i18n.t(PROFILE_TYPE_LABELS[type]))
            .join(', '),
  }),
  columnHelper.accessor('dismissal_count', {
    header: () => i18n.t('admin.notices.col.dismissals'),
  }),
];

function Actions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language, state } = useSearch({ strict: false }) as {
    language?: Language;
    state?: State;
  };
  const set = (patch: Record<string, unknown>) =>
    void navigate({
      to: './',
      replace: true,
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        ...patch,
        page: undefined,
      })) as never,
    });
  return (
    <Bar>
      <Select
        aria-label={t('admin.common.language')}
        value={language ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          set({ language: e.target.value || undefined })
        }
      >
        <option value="">{t('admin.help.allLanguages')}</option>
        {LANGUAGE_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {t(label)}
          </option>
        ))}
      </Select>
      <Select
        aria-label={t('admin.notices.col.state')}
        value={state ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          set({ state: e.target.value || undefined })
        }
      >
        <option value="">{t('admin.notices.allStates')}</option>
        {STATES.map((value) => (
          <option key={value} value={value}>
            {t(`admin.notices.state.${value}`)}
          </option>
        ))}
      </Select>
      <Link to="/admin/notices/new/" className={buttonStyles.Button}>
        {t('admin.notices.newNotice')}
      </Link>
    </Bar>
  );
}

const NoticeList = createTablePage<Item, '/admin/notices/$noticeId'>({
  name: 'admin.nav.notices',
  urlState: true,
  searchable: true,
  searchPlaceholder: 'admin.notices.searchPlaceholder',
  useData: (_page, search, { paging }) => {
    const { language, state } = useSearch({ strict: false }) as {
      language?: Language;
      state?: State;
    };
    return api.listAdminHeaderNotices.useQuery({
      query: { ...paging, q: search || undefined, language, state },
    });
  },
  columns,
  itemBaseUrl: '/admin/notices/$noticeId',
  getParams: (row) => ({ noticeId: String(row.id) }),
  actions: <Actions />,
});

export const Route = createFileRoute('/admin/notices/')({
  staticData: { minTier: 'SUPPORT' },
  validateSearch: (
    s: Record<string, unknown>,
  ): { language?: Language; state?: State } & Record<string, unknown> => ({
    ...tableSearchKeys(s),
    language: parseLanguage(s.language),
    state: STATES.find((value) => value === s.state),
  }),
  component: NoticeList,
});

const Bar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;
